import { SHOP_CURRENCIES, ShopCurrencySchema } from "@/shared/lib/currency";
import {
  FormInput,
  FormRoot,
  FormSelect,
  RHF,
  Switch,
  zodResolver,
} from "@devhop/ui";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { z } from "zod";

const SHOP_CURRENCY_OPTIONS = SHOP_CURRENCIES.map((c) => ({
  value: c,
  label: c,
}));

const ShopFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຮ້ານ"),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  taxId: z.string().max(64).optional(),
  defaultTaxPercent: z.coerce
    .number()
    .min(0, "ອັດຕາພາສີຕ້ອງບໍ່ຕ່ຳກວ່າ 0")
    .max(999.99),
  defaultCurrency: ShopCurrencySchema,
  /** ບັງຄັບໃຫ້ສະກຸນ SKU ກົງກັບສະກຸນຮ້ານ */
  enforceSingleShopSkuCurrency: z.boolean(),
});

export type ShopFormValues = z.infer<typeof ShopFormSchema>;

export type ShopProfileFormRef = { submit: () => void };

export const ShopProfileForm = forwardRef<
  ShopProfileFormRef,
  {
    initialValues: ShopFormValues;
    onSubmit: (values: ShopFormValues) => void;
    submitting?: boolean;
  }
>(function ShopProfileForm({ initialValues, onSubmit, submitting: _submitting }, ref) {
  const submitRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => ({
    submit: () => submitRef.current?.click(),
  }));

  const methods = RHF.useForm<ShopFormValues>({
    resolver: zodResolver(ShopFormSchema) as RHF.Resolver<ShopFormValues>,
    defaultValues: initialValues,
  });

  return (
    <FormRoot<ShopFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput name="name" label="ຊື່ຮ້ານ" placeholder="ຊື່ຮ້ານສະແດງ" />
        <FormInput name="phone" label="ເບີໂທ" placeholder="+856 20 xxxx xxxx" />
      </div>
      <FormInput name="address" label="ທີ່ຢູ່" placeholder="ບ້ານ ເມືອງ ແຂວງ" />

      <FormInput name="city" label="ເມືອງ / ແຂວງ" placeholder="ວຽງຈັນ" />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput
          name="taxId"
          label="ເລກປະຈຳຕົວຜູ້ເສຍພາສີ"
          placeholder="ສຳລັບໃບກຳກັບ"
        />
        <FormInput
          name="defaultTaxPercent"
          type="number"
          step="0.01"
          min={0}
          label="ອັດຕາພາສີເລີ່ມຕົ້ນ POS (%)"
          placeholder="0"
        />
      </div>
      <FormSelect
        name="defaultCurrency"
        label="ສະກຸນເງິນເລີ່ມຕົ້ນ (POS / SKU)"
        options={SHOP_CURRENCY_OPTIONS}
      />
      <div className="flex items-center gap-2">
        <RHF.Controller
          control={methods.control}
          name="enforceSingleShopSkuCurrency"
          render={({ field }) => (
            <Switch
              id="shop-enforce-sku-currency"
              checked={field.value}
              onCheckedChange={field.onChange}
              className="data-[state=checked]:bg-[#F26522]"
            />
          )}
        />
        <label
          htmlFor="shop-enforce-sku-currency"
          className="font-medium text-sm"
        >
          ເປີດນຳໃຊ້ ຫຼາຍສະກຸນເງິນ ໃນຄີສິນຄ້າເຂົ້າ
        </label>
      </div>
      <button
        ref={submitRef}
        type="submit"
        className="hidden"
        tabIndex={-1}
        aria-hidden
      />
    </FormRoot>
  );
});
